/**
 * [クライアント用]AmazonPayボタンをレンダリング・制御するクライアント
 */
export default class AmazonPayClient {
  merchantId: string
  publicKeyId: string
  payloadFile: string
  signInPayloadFile: string
  signature: string
  sessionId = ""
  selectorId = ""
  route = useRoute()

  constructor(options?: { id?: string }) {
    // get runtime config env
    const config = useRuntimeConfig()

    // specify selector id
    const id = options?.id || "AmazonPayButton"
    this.selectorId = id

    // amazonpay env
    const amazonPayPayloadFile = config.public.AMAZONPAY_PAYLOAD_FILE_STRING_JSON || ""
    const amazonPaySignInPayloadFile = config.public.AMAZONPAY_SIGNIN_PAYLOAD_FILE_STRING_JSON || ""
    const amazonPaySignature = config.public.AMAZONPAY_SIGNATURE || ""
    const amazonPayMerchantId = config.public.AMAZONPAY_MERCHANT_ID || ""
    const amazonPayPublicKeyId = config.public.AMAZONPAY_PUBLIC_KEY_ID || ""
    const amazonPayStoreId = config.public.AMAZONPAY_STORE_ID || ""
    this.merchantId = amazonPayMerchantId
    this.publicKeyId = amazonPayPublicKeyId
    this.payloadFile = amazonPayPayloadFile
    this.signature = amazonPaySignature
    this.signInPayloadFile = amazonPaySignInPayloadFile
    const storeId = amazonPayStoreId

    if (process.server) return
    // append checkout script
    const checkoutScript = document.createElement("script")
    const amazonCheckoutSrc = "https://static-fe.payments-amazon.com/checkout.js"
    checkoutScript.setAttribute("src", amazonCheckoutSrc)
    document.body.appendChild(checkoutScript)

    // bind change action option
    const bindChangeAction = this.route.fullPath.includes("order/new")
    const bindChangeAddressAction = this.route.fullPath.includes("order/new")

    // append recaptcha script
    const recaptchaScript = document.createElement("script")
    // 500msごとにレンダリング対象DOM、SDKの読み込みを監視 -> AmazonPayボタンのレンダリングを実施
    // 40回(20秒)再試行しても読み込みが完了しない場合は処理を終了
    // renderButton${id}等の関数が存在しない場合を考慮し、clearIntervalは最後に実行
    recaptchaScript.innerHTML = `
      var retryCount${id} = 0;
      var interval${id} = setInterval(() => {
        try {
          const elem = document.getElementById('${this.selectorId}');
          if (elem && amazon && amazon.Login) {
            amazon.Login.setClientId('${storeId}');
            renderButton${id}();
            clearInterval(interval${id});
            ${bindChangeAction ? "bindChangeAction();" : ""}
            ${bindChangeAddressAction ? "bindChangeAddressAction();" : ""}
          }
        } catch(e) {
          retryCount${id}++;
          if (retryCount${id} >= 40) {
            clearInterval(interval${id});
            console.error('Amazon Pay recaptchaScript failed after 40 retries');
            const elem = document.getElementById('${this.selectorId}');
            if (elem) {
              elem.innerHTML = 'Amazon Payボタンの読み込みに失敗しました。ページをリロードしてください。';
            }
          }
        }
      }, 500);
      `
    document.head.appendChild(recaptchaScript)

    // append amazonpay widget script
    const amazonPayWidgetUrl = "https://static-fe.payments-amazon.com/OffAmazonPayments/jp/sandbox/lpa/js/Widgets.js"
    const amazonPayWidgetScript = document.createElement("script")
    const amazonPayWidgetScriptSrc = amazonPayWidgetUrl + "?sellerId=" + this.merchantId
    amazonPayWidgetScript.setAttribute("async", "async")
    amazonPayWidgetScript.setAttribute("src", amazonPayWidgetScriptSrc)
    document.head.appendChild(amazonPayWidgetScript)
  }

  /**
   * AmazonPayサインインボタンのレンダリング
   * @param _selector
   * @param _options
   */
  renderButton(_options: {
    placement: "Home" | "Product" | "Cart" | "Checkout" | "Other"
    ledgerCurrency: "USD" | "EUR" | "GBP" | "JPY"
    buttonColor?: "Gold" | "LightGray" | "DarkGray"
    checkoutLanguage?: "en_US" | "en_GB" | "fr_FR" | "it_IT" | "es_ES" | "ja_JP"
    sandbox?: boolean
    width?: number
    height?: number
    productType?: "PayAndShip" | "SignIn"
    signature?: string
  }): void {
    const renderButtonScript = document.createElement("script")
    renderButtonScript.setAttribute("type", "text/javascript")
    renderButtonScript.setAttribute("charset", "utf-8")
    const productType = _options.productType || "PayAndShip"

    let innerHTML = `
      function renderButton${this.selectorId}() {
        const hostElement = document.querySelector('#${this.selectorId}');
        if (hostElement.shadowRoot) {
          return;
        }
        amazon.Pay.renderButton('#${this.selectorId}', {
          merchantId: '${this.merchantId}',
          ledgerCurrency: '${_options.ledgerCurrency}',
          placement: '${_options.placement}',
          productType: '${productType}',`

    if (_options.buttonColor) innerHTML += `buttonColor: '${_options.buttonColor}',`
    if (_options.checkoutLanguage) innerHTML += `checkoutLanguage: '${_options.checkoutLanguage}',`
    if (_options.sandbox) innerHTML += `sandbox: ${_options.sandbox},`

    // For import, you must access default
    // const payload = await import(`./payloads/${this.payloadFileName}`)
    // const json = JSON.stringify(payload.default)
    const config = useRuntimeConfig()

    if (productType === "SignIn") {
      const json =
        config.public.APP_ENV === "development" ? JSON.stringify(this.signInPayloadFile) : this.signInPayloadFile
      innerHTML += `
          signInConfig: {                     
            payloadJSON: '${json}',
            signature: '${_options.signature || this.signature}',
            publicKeyId: '${this.publicKeyId}' 
          }
        });
      }`
    } else {
      const json = config.public.APP_ENV === "development" ? JSON.stringify(this.payloadFile) : this.payloadFile
      innerHTML += `
          createCheckoutSessionConfig: {
            payloadJSON: '${json}',
            signature: '${_options.signature || this.signature}',
            publicKeyId: '${this.publicKeyId}'
          }
        });
      }`
    }

    renderButtonScript.innerHTML = innerHTML
    document.body.appendChild(renderButtonScript)
  }

  /**
   * AmazonPayのセッションIDをセット
   * @param _sessionId
   */
  setSessionId = (_sessionId: string): void => {
    this.sessionId = _sessionId
  }

  /**
   * AmazonPayのセレクターDを取得
   * @param _sessionId
   */
  getSelectorId = (): string => {
    return this.selectorId
  }

  /**
   * AmazonPayの住所を変更
   * @param _selector
   */
  handleAddressChange = async (_selector: string): Promise<void> => {
    const handleChangeScript = document.createElement("script")
    handleChangeScript.setAttribute("type", "text/javascript")
    handleChangeScript.setAttribute("charset", "utf-8")

    let selector = _selector
    if (/^#/.test(selector)) selector = selector.slice(1)

    for (let i = 0; i <= 10; i++) {
      if (document.getElementById(selector)) {
        const innerHTML = `
        function bindChangeAddressAction() {
          amazon.Pay.bindChangeAction('${_selector}', {
            amazonCheckoutSessionId: '${this.sessionId}',
            changeAction: 'changeAddress'
          });
        }`

        handleChangeScript.innerHTML = innerHTML
        document.body.appendChild(handleChangeScript)
        break
      } else {
        await new Promise((resolve) => setTimeout(resolve, 500))
      }
    }
  }

  /**
   * AmazonPayの支払い方法を変更
   * @param _selector
   */
  handlePaymentChange = async (_selector: string): Promise<void> => {
    const handleChangeScript = document.createElement("script")
    handleChangeScript.setAttribute("type", "text/javascript")
    handleChangeScript.setAttribute("charset", "utf-8")

    let selector = _selector
    if (/^#/.test(selector)) selector = selector.slice(1)

    for (let i = 0; i <= 10; i++) {
      if (document.getElementById(selector)) {
        const innerHTML = `
        function bindChangeAction() {
          amazon.Pay.bindChangeAction('${_selector}', {
            amazonCheckoutSessionId: '${this.sessionId}',
            changeAction: 'changePayment'
          });
        }`

        handleChangeScript.innerHTML = innerHTML
        document.body.appendChild(handleChangeScript)
        break
      } else {
        await new Promise((resolve) => setTimeout(resolve, 500))
      }
    }
  }
}
